import type { ReactElement } from 'react';
import React from 'react';
import Alert from 'atom-components/alert';
import SystemErrorAlert, { systemError } from 'feature-components/system-error-alert';
import { friendlyRole } from 'helpers/domain-specific/role.helpers';
import { joinWithConjunction } from 'helpers/string.helpers';
import { type AuthorisableMark } from 'policies/entities/mark';
import MarkPolicy, {
  independentMarkerRoles,
  userIsActiveMarker,
  userIsAllocatedToResponse
} from 'policies/mark.policy';
import { hasMarkingAllocationRole } from 'policies/marking-allocation.policy';
import { hasMarkingTaskRole } from 'policies/marking-task.policy';
import type { MeCustomerUser } from 'services/assist-api/entities/me/me';
import { ROLE } from 'services/assist-api/entities/role/roles';
import {
  MarkStatus,
  MarkType,
  type TestInstanceResponseMarkWithItems
} from 'services/assist-api/entities/test-instance-response-mark/types';
import type { TestInstanceResponseMarkerWithCustomerUser } from 'services/assist-api/entities/test-instance-response-marker/types';
import type { TestInstanceResponseWithResponseItemsAndMediaObjects } from 'services/assist-api/entities/test-instance-response/types';
import type { BaseTestInstance } from 'services/assist-api/entities/test-instance/types';
import type { TestRubricWithMediaObjects } from 'services/assist-api/entities/test-rubric/types';
import { usePageAccess } from 'services/providers/page-access.provider';
import AutoscoringIssues from './autoscoring-issues';
import InfoSection from './info-section';
import MarkResponseFormLoader from './mark-response-form-loader';

type Props = {
  onSave: () => void;
  assessment: BaseTestInstance;
  response: TestInstanceResponseWithResponseItemsAndMediaObjects;
  responseMarkers: TestInstanceResponseMarkerWithCustomerUser[];
  rubric: TestRubricWithMediaObjects;
  currentOrganisationUser: MeCustomerUser;
  mark?: TestInstanceResponseMarkWithItems;
};

function getCurrentUserMarker(
  responseMarkers: TestInstanceResponseMarkerWithCustomerUser[],
  currentOrganisationUser: MeCustomerUser
): TestInstanceResponseMarkerWithCustomerUser | undefined {
  return responseMarkers.find((m) => m.user.id === currentOrganisationUser.id);
}

export function getActiveMarker(
  responseMarkers: TestInstanceResponseMarkerWithCustomerUser[],
  currentOrganisationUser: MeCustomerUser,
  mark?: TestInstanceResponseMarkWithItems
): TestInstanceResponseMarkerWithCustomerUser | undefined {
  // We can consider the active marker to be the current user marker who has already been allocated
  // to the response if there is no mark. This allows the user to be implicitly set as the active
  // marker if they are starting a new mark from a "Create Mark" marking task on the Marker
  // Dashboard.
  return mark
    ? responseMarkers.find((m) => m.id === mark?.activeMarker?.id)
    : getCurrentUserMarker(responseMarkers, currentOrganisationUser);
}

function noActiveMarkerMessage(markExists: boolean): string {
  return `There is no active marker. ${
    markExists
      ? systemError('A mark should always have an active marker.')
      : 'A marking admin needs to assign a marker to the response in order for marking of it to be possible.'
  }`;
}

export default function MarkResponseAuthoriser({
  onSave,
  assessment,
  response,
  responseMarkers,
  rubric,
  currentOrganisationUser,
  mark
}: Props): ReactElement {
  const { authorisableUser } = usePageAccess();

  const currentUserMarker = getCurrentUserMarker(responseMarkers, currentOrganisationUser);
  const activeMarker = getActiveMarker(responseMarkers, currentOrganisationUser, mark);

  const authorisableMark: AuthorisableMark = {
    id: mark?.id,
    type: mark?.type || MarkType.Manual,
    activeMarkerId: activeMarker?.id,
    status: mark?.status || MarkStatus.Assigned,
    responseMarkers
  };

  const isAllocatedToResponse = userIsAllocatedToResponse(authorisableUser, authorisableMark);
  const isActiveMarker = userIsActiveMarker(authorisableUser, authorisableMark);

  const canAdministrate = MarkPolicy.administrate(authorisableUser, authorisableMark);
  const canMarkIndependently = MarkPolicy.markIndependently(authorisableUser, authorisableMark);

  return (
    <>
      <InfoSection
        authorisableMark={authorisableMark}
        isActiveMarker={isActiveMarker}
        activeMarker={activeMarker}
        rubricMediaObjects={rubric?.mediaObjects || []}
        responseMediaObjects={response?.mediaObjects || []}
        markStatus={mark?.status ?? MarkStatus.Assigned}
      />
      {mark?.type === MarkType.Autoscored ? (
        <Alert variant="info">Autoscored marks cannot be modified.</Alert>
      ) : (
        <>
          {canAdministrate && (
            <Alert variant="info">
              You have a {friendlyRole(ROLE.MARKING_ADMIN)} or a higher role. You can make changes
              to marks regardless of allocation. You will be automatically allocated to the response
              and set as the active marker when the form is saved.
            </Alert>
          )}
          {!canAdministrate && canMarkIndependently && (
            <Alert variant="info">
              You have an{' '}
              {joinWithConjunction(
                independentMarkerRoles
                  .map((role) => friendlyRole(role))
                  .filter((role) => role !== undefined),
                'or'
              )}{' '}
              role. You can start marking independently and resume draft marks started by other
              markers. You will be automatically set as the active marker when the form is saved.
            </Alert>
          )}
          {!canAdministrate && !canMarkIndependently && !isAllocatedToResponse && (
            <Alert variant="danger">
              You have not been been assigned as a marker of this response.
            </Alert>
          )}
          {!canMarkIndependently && !hasMarkingTaskRole(authorisableUser) && (
            <SystemErrorAlert>
              You have somehow reached this page without a marking task role and you cannot mark
              independently.
            </SystemErrorAlert>
          )}
          {!activeMarker && !hasMarkingAllocationRole && (
            <Alert variant="danger">{noActiveMarkerMessage(mark !== undefined)}</Alert>
          )}
        </>
      )}
      {mark?.type === MarkType.Autoscored && <AutoscoringIssues mark={mark} />}
      {MarkPolicy.viewForm(authorisableUser, authorisableMark) && (
        <MarkResponseFormLoader
          onSave={onSave}
          assessment={assessment}
          response={response}
          rubricId={rubric.id}
          authorisableMark={authorisableMark}
          currentOrganisationUser={currentOrganisationUser}
          currentUserMarker={currentUserMarker}
          activeMarker={activeMarker}
          mark={mark}
        />
      )}
    </>
  );
}
